import React, { useMemo } from "react";
import { Card, Col } from "react-bootstrap";
import ApexCharts from "react-apexcharts";
import { useSelector } from "react-redux";
import { useAppContext } from "providers/AppProvider";

const PreAuthApprovalRateChart = () => {
  const { generalDashboardData } = useSelector((state) => state.dashboard);
  const { config: { isDark } } = useAppContext();

  const processedData = useMemo(() => {
    const rawData = generalDashboardData?.preauth_dept || [];

    return rawData.map(dept => {
      const stats = dept.tot_status || {};

      // Calculate Approved Group
      const approved =
        (Number(stats.approved) || 0)
      //  +
      // (Number(stats['Partially Approved']) || 0) +
      // (Number(stats['not-required']) || 0);

      // Calculate Rejected Group
      const rejected = Number(stats.rejected) || 0;

      return {
        name: dept.dept_name || "",
        approved,
        rejected,
        total: approved + rejected
      };
    })
      .filter(item => item.total > 0) // Filter out departments with no activity
      .sort((a, b) => b.total - a.total); // Sort by volume descending
  }, [generalDashboardData]);

  const chartOptions = useMemo(() => {
    const categories = processedData.map(d => d.name);
    const approvedData = processedData.map(d => d.approved);
    const rejectedData = processedData.map(d => d.rejected);

    return {
      series: [
        { name: "APPROVED", data: approvedData },
        { name: "REJECTED", data: rejectedData },
      ],
      options: {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: { show: false },
          fontFamily: 'Inter, sans-serif'
        },
        colors: ["#10b981", "#ef4444"], // Green and Red
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            barHeight: "30%",
            dataLabels: { position: "top" },
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: categories,
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: {
              colors: "#64748b",
              fontSize: "11px",
              fontWeight: 600,
            },
            maxWidth: 400,
            minWidth: 120,
            formatter: (text) => {
              if (typeof text !== 'string') return text;
              const limit = 25;
              const words = text.split(" ");
              let lines = [];
              let currentLine = words[0];

              for (let i = 1; i < words.length; i++) {
                if (currentLine.length + words[i].length + 1 <= limit) {
                  currentLine += " " + words[i];
                } else {
                  lines.push(currentLine);
                  currentLine = words[i];
                }
              }
              lines.push(currentLine);
              return lines;
            }
          },
        },
        grid: {
          show: false,
          padding: { top: 0, right: 0, bottom: 0, left: 10 }
        },
        legend: {
          show: false
        },
        tooltip: {
          theme: isDark ? "dark" : "light",
          y: {
            formatter: (val, { series, seriesIndex, dataPointIndex, w }) => {
              const total = w.globals.stackedSeriesTotals[dataPointIndex];
              const percentage = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
              return `${percentage}%`;
            }
          }
        },
        fill: { opacity: 1 }
      },
    };
  }, [processedData]);

  // if (!processedData?.length) return null;

  return (
    <Col xs={12}>
      <Card className="border-0 shadow-sm rounded-4 h-100">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <h5 className="mb-1 fw-bold fs-9 text-900">
                Clinical Department Throughput
              </h5>
              <p className="mb-0 text-500 fs-11">
                Distribution of approved and rejected authorization across departments              </p>
            </div>
            <div className="d-flex gap-3 align-items-center mt-1">
              <div className="d-flex align-items-center gap-2">
                <div className="rounded-circle" style={{ width: '8px', height: '8px', backgroundColor: '#10b981' }}></div>
                <span className="fs-11 fw-bold text-600">APPROVED</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div className="rounded-circle" style={{ width: '8px', height: '8px', backgroundColor: '#ef4444' }}></div>
                <span className="fs-11 fw-bold text-600">REJECTED</span>
              </div>
            </div>
          </div>

          <ApexCharts
            options={chartOptions.options}
            series={chartOptions.series}
            type="bar"
            width="100%"
            height={Math.max(processedData.length * 50 + 50, 300)}
          />
        </Card.Body>
      </Card>
    </Col>
  );
};

export default PreAuthApprovalRateChart;
